import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { DIALOG_TYPE } from '../../../../constants/DialogTypes';
import { Dialog } from '../../../../ui/components/Common/Dialog/Dialog';
import { getJobOpeningList } from '../../../../utils/api_service/JobOpenings';
import { CandiBankFolder } from '../../../../utils/types/candi_bank_types';

const MAX_NAME = 100;

type Option = { value: string; label: string };

const selectStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 10050 }),
  control: (base: any, state: any) => ({
    ...base,
    minHeight: '40px',
    borderRadius: '8px',
    border: `1px solid ${state.isFocused ? '#6366f1' : '#e2e8f0'}`,
    boxShadow: state.isFocused ? '0 0 0 3px rgba(99,102,241,0.12)' : 'none',
  }),
  option: (base: any) => ({ ...base, fontSize: '13px' }),
  placeholder: (base: any) => ({ ...base, color: '#94a3b8', fontSize: '13px' }),
  singleValue: (base: any) => ({ ...base, fontSize: '13px', color: '#334155' }),
};

interface FolderFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Present when editing; absent when creating. */
  folder?: CandiBankFolder | null;
  onSubmit: (name: string, jobOpeningId: string | null) => Promise<unknown>;
}

/** Create or edit a folder: a name and an optional linked job opening. */
export const FolderFormModal: React.FC<FolderFormModalProps> = ({ isOpen, onClose, folder, onSubmit }) => {
  const [name, setName] = useState('');
  const [opening, setOpening] = useState<Option | null>(null);
  const [openings, setOpenings] = useState<Option[]>([]);
  const [loadingOpenings, setLoadingOpenings] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(folder?.name ?? '');
    setOpening(
      folder?.jobOpeningId
        ? { value: folder.jobOpeningId, label: folder.jobOpeningTitle ?? 'Linked job opening' }
        : null,
    );
    setLoadingOpenings(true);
    getJobOpeningList({} as any, 1, 50)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) =>
        setOpenings(
          (data.jobOpenings ?? []).map((o: any) => ({
            value: o.id,
            label: o.jobReference ? `${o.jobTitle ?? 'Untitled'} · ${o.jobReference}` : o.jobTitle ?? 'Untitled',
          })),
        ),
      )
      .catch(() => setOpenings([]))
      .finally(() => setLoadingOpenings(false));
  }, [isOpen, folder]);

  const trimmed = name.trim();
  const unchanged =
    !!folder && trimmed === folder.name && (opening?.value ?? null) === (folder.jobOpeningId ?? null);

  const handleSubmit = async () => {
    if (!trimmed || unchanged) return;
    setSubmitting(true);
    try {
      const result = await onSubmit(trimmed, opening?.value ?? null);
      if (result) onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
        isOpen={isOpen}
        onClose={onClose}
        handleApply={handleSubmit}
        title={folder ? 'Edit Folder' : 'New Folder'}
        confirmButtonLable={submitting ? 'Saving...' : folder ? 'Save' : 'Create Folder'}
        cancelButtonLable='Cancel'
        dialogType={DIALOG_TYPE.SUCCESS}
        maxWidth='460px'
        disabled={!trimmed || unchanged || submitting}
    >
      <div className='flex flex-col gap-5 py-1'>
        <div className='flex flex-col gap-1.5'>
          <label htmlFor='folder-name' className='text-xs font-bold text-slate-600'>
            Folder name
          </label>
          <input
              id='folder-name'
              autoFocus
              value={name}
              maxLength={MAX_NAME}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
              }}
              placeholder='e.g. Java seniors for Q4'
              className='w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 text-sm text-slate-700'
          />
        </div>
        <div className='flex flex-col gap-1.5'>
          <label className='text-xs font-bold text-slate-600'>
            Job opening <span className='font-medium text-slate-400'>(optional)</span>
          </label>
          <Select
              options={openings}
              value={opening}
              onChange={(opt: any) => setOpening(opt)}
              isClearable
              isLoading={loadingOpenings}
              placeholder='Not linked to an opening'
              menuPortalTarget={document.body}
              styles={selectStyles}
              noOptionsMessage={() => 'No job openings found'}
          />
          <p className='text-[11px] text-slate-400'>
            Use a folder for a role that has no job opening yet, or link it to one.
          </p>
        </div>
      </div>
    </Dialog>
  );
};
